/**
 * Input sanitization utilities to prevent XSS, injection, and other attacks.
 * Applied before sending form data to any service.
 *
 * Defence-in-depth: these run client-side. Always validate server-side too
 * (Supabase RLS policies, EmailJS template restrictions, etc.)
 */

// ─── HTML Entity Decoding (to catch encoded bypass attempts) ──────────────────

const HTML_ENTITIES = {
    '&lt;': '<', '&gt;': '>', '&amp;': '&', '&quot;': '"',
    '&#x27;': "'", '&#x2F;': '/', '&#39;': "'", '&#47;': '/',
    '&apos;': "'",
};

/**
 * Decode common HTML entities so encoded payloads don't slip through.
 * @param {string} str
 * @returns {string}
 */
function decodeHTMLEntities(str) {
    let decoded = str;
    // Decode named entities
    for (const [entity, char] of Object.entries(HTML_ENTITIES)) {
        decoded = decoded.replace(new RegExp(entity, 'gi'), char);
    }
    // Decode numeric entities (decimal &#60; and hex &#x3C;)
    decoded = decoded.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)));
    decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
    return decoded;
}

// ─── Core Sanitization ───────────────────────────────────────────────────────

/**
 * Strip HTML tags, dangerous protocols, and attack patterns from a string.
 * Handles encoding bypasses by first decoding, then stripping.
 * @param {string} input
 * @returns {string}
 */
export function sanitizeText(input) {
    if (typeof input !== 'string') return '';

    let clean = input;

    // 1. Remove null bytes (used to bypass filters)
    clean = clean.replace(/\0/g, '');

    // 2. Decode HTML entities to catch encoded XSS
    clean = decodeHTMLEntities(clean);

    // 3. Remove HTML tags (including self-closing and malformed)
    clean = clean.replace(/<\/?[^>]+(>|$)/g, '');

    // 4. Remove remaining angle brackets
    clean = clean.replace(/[<>]/g, '');

    // 5. Remove dangerous protocols (javascript:, vbscript:, data:text/html)
    clean = clean.replace(/\b(javascript|vbscript|data)\s*:/gi, '');

    // 6. Remove inline event handlers (onclick=, onerror=, etc.)
    clean = clean.replace(/\bon\w+\s*=/gi, '');

    // 7. Remove expression() CSS attack vector
    clean = clean.replace(/expression\s*\(/gi, '');

    // 8. Remove embedded script-like content
    clean = clean.replace(/\b(eval|alert|confirm|prompt|Function)\s*\(/gi, '');

    // 9. Collapse excessive whitespace
    clean = clean.replace(/\s{2,}/g, ' ');

    return clean.trim();
}

/**
 * Strict sanitization that only allows alphanumeric + basic punctuation.
 * Use for fields like names, cities, roles where free HTML is never expected.
 * @param {string} input
 * @param {number} maxLength
 * @returns {string}
 */
export function sanitizeStrictText(input, maxLength = 200) {
    if (typeof input !== 'string') return '';
    // Allow letters (any script), digits, spaces, hyphens, periods, commas, apostrophes
    const clean = input
        .replace(/[^\p{L}\p{N}\s\-.,''&()/@#]/gu, '')
        .trim()
        .slice(0, maxLength);
    return clean;
}

// ─── Validation Functions ────────────────────────────────────────────────────

/**
 * Validate email format (RFC 5322 simplified).
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
    if (typeof email !== 'string') return false;
    const trimmed = email.trim().toLowerCase();
    if (trimmed.length > 254 || trimmed.length < 5) return false;
    // Must match pattern and not contain dangerous characters
    const pattern = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;
    return pattern.test(trimmed);
}

/**
 * Validate phone number (international format).
 * @param {string} phone
 * @returns {boolean}
 */
export function isValidPhone(phone) {
    if (typeof phone !== 'string') return false;
    const cleaned = phone.replace(/[\s\-().]/g, '');
    return /^\+?\d{7,15}$/.test(cleaned);
}

/**
 * Validate a URL (only allow http/https protocols).
 * @param {string} url
 * @returns {boolean}
 */
export function isValidURL(url) {
    if (typeof url !== 'string' || url.length === 0) return true; // Empty = optional
    if (url.length > 2048) return false;
    try {
        const parsed = new URL(url);
        return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
        return false;
    }
}

/**
 * Validate that a string doesn't exceed max length and isn't just whitespace.
 * @param {string} input
 * @param {number} maxLength
 * @param {number} minLength
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateLength(input, maxLength = 500, minLength = 1) {
    if (typeof input !== 'string') return { valid: false, reason: 'Input must be a string' };
    const trimmed = input.trim();
    if (trimmed.length < minLength) return { valid: false, reason: `Minimum ${minLength} character(s) required` };
    if (trimmed.length > maxLength) return { valid: false, reason: `Maximum ${maxLength} characters allowed` };
    return { valid: true };
}

// ─── Form-level Sanitization ─────────────────────────────────────────────────

/**
 * Sanitize all string values in a form object.
 * @param {Object} formData
 * @returns {Object}
 */
export function sanitizeFormData(formData) {
    const sanitized = {};
    for (const [key, value] of Object.entries(formData)) {
        if (typeof value === 'string') {
            sanitized[key] = sanitizeText(value);
        } else {
            sanitized[key] = value;
        }
    }
    return sanitized;
}

/**
 * Sanitize form data with field-specific rules.
 * @param {Object} formData
 * @param {Object} fieldRules - Map of field name to { maxLength, type }
 * @returns {Object}
 */
export function sanitizeFormDataStrict(formData, fieldRules = {}) {
    const sanitized = {};
    for (const [key, value] of Object.entries(formData)) {
        if (typeof value !== 'string') {
            sanitized[key] = value;
            continue;
        }
        const rule = fieldRules[key] || {};
        const maxLen = rule.maxLength || 500;

        if (rule.type === 'email') {
            sanitized[key] = value.trim().toLowerCase().slice(0, 254);
        } else if (rule.type === 'url') {
            sanitized[key] = value.trim().slice(0, 2048);
        } else if (rule.type === 'strict') {
            sanitized[key] = sanitizeStrictText(value, maxLen);
        } else {
            sanitized[key] = sanitizeText(value).slice(0, maxLen);
        }
    }
    return sanitized;
}

// ─── Prototype Pollution Guard ───────────────────────────────────────────────

/**
 * Check if an object key is a prototype pollution attempt.
 * Use when accepting dynamic keys (e.g., from JSON parsing).
 * @param {string} key
 * @returns {boolean}
 */
export function isSafeObjectKey(key) {
    const forbidden = ['__proto__', 'constructor', 'prototype'];
    return !forbidden.includes(key);
}
